import React, { useState, useEffect } from "react";
import {
  Card,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import SemiCircleProgress from "./SemiCircleProgress";
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";
import VuiButton from "components/VuiButton";

const AsphaltBatchQuality = ({ batchQualityData, site_id, productionCardSize }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [targetQuality, setTargetQuality] = useState(95); // Default target quality percentage

  const calculatePercentages = (data) => {
    if (!data) return null;

    // Convert string values to numbers
    const dayGood = parseInt(data.Day_Good_Quality) || 0;
    const dayBad = parseInt(data.Day_Bad_Quality) || 0;
    const weekGood = parseInt(data.Week_Good_Quality) || 0;
    const weekBad = parseInt(data.Week_Bad_Quality) || 0;
    const monthGood = parseInt(data.Month_Good_Quality) || 0;
    const monthBad = parseInt(data.Month_Bad_Quality) || 0;
    const yearGood = parseInt(data.Year_Good_Quality) || 0;
    const yearBad = parseInt(data.Year_Bad_Quality) || 0;

    return {
      day: {
        good: dayGood,
        bad: dayBad,
        percentage: dayGood + dayBad > 0 ? Math.round((dayGood / (dayGood + dayBad)) * 100) : 0,
      },
      week: {
        good: weekGood,
        bad: weekBad,
        percentage:
          weekGood + weekBad > 0 ? Math.round((weekGood / (weekGood + weekBad)) * 100) : 0,
      },
      month: {
        good: monthGood,
        bad: monthBad,
        percentage:
          monthGood + monthBad > 0 ? Math.round((monthGood / (monthGood + monthBad)) * 100) : 0,
      },
      year: {
        good: yearGood,
        bad: yearBad,
        percentage:
          yearGood + yearBad > 0 ? Math.round((yearGood / (yearGood + yearBad)) * 100) : 0,
      },
    };
  };

  const [qualityData, setQualityData] = useState(null);

  useEffect(() => {
    if (batchQualityData) {
      const percentages = calculatePercentages(batchQualityData);
      setQualityData(percentages);
    }
  }, [batchQualityData]);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  const handleUpdate = async () => {
    const payload = {
      site_id: site_id,
      target_quality: targetQuality,
    };

    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/update-quality-target",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        console.log("Quality target updated successfully");
        setOpenDialog(false);
      } else {
        console.error("Failed to update quality target");
      }
    } catch (error) {
      console.error("Error updating quality target:", error);
    }
  };

  const { gradients } = colors;
  const { cardContent } = gradients;

  const getSemiCircleSize = () => {
    switch (true) {
      case productionCardSize <= 3: // Smaller grid sizes
        return {
          day: { width: 200, height: 180 },
          secondary: { width: 100, height: 90 },
          infoBoxMargin: {
            day: "-40px",
            secondary: "-30px",
          },
        };
      case productionCardSize <= 6: // Medium grid sizes
        return {
          day: { width: 250, height: 220 },
          secondary: { width: 130, height: 110 },
          infoBoxMargin: {
            day: "-50px",
            secondary: "-35px",
          },
        };
      default: // Larger grid sizes
        return {
          day: { width: 280, height: 250 },
          secondary: { width: 150, height: 130 },
          infoBoxMargin: {
            day: "-60px",
            secondary: "-40px",
          },
        };
    }
  };

  const sizes = getSemiCircleSize();

  const InfoBox = ({ good, bad, label, sx, margin }) => (
    <VuiBox
      sx={{
        width: "90%",
        padding: "4px 8px",
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        borderRadius: "8px",
        background: linearGradient(
          colors.gradients.cardContent.main,
          colors.gradients.cardContent.state,
          colors.gradients.cardContent.deg
        ),
        position: "relative",
        zIndex: 10,
        marginTop: margin || 0,
        ...sx,
      }}
    >
      <VuiTypography color="text" variant="caption" display="inline-block">
        0%
      </VuiTypography>
      <VuiBox
        flexDirection="column"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ flex: 1, textAlign: "center" }}
      >
        <VuiTypography
          color="white"
          variant="h5"
          sx={{
            fontSize: {
              xs: "0.7rem",
              sm: "0.9rem",
              md: "1rem",
            },
          }}
        >
          <span style={{ color: "#4CAF50" }}>{good}</span>
          <br />
          <span style={{ color: "#ea3546" }}>{bad}</span>{" "}
        </VuiTypography>
        <VuiTypography color="text" variant="caption" display="inline-block">
          {label}
        </VuiTypography>
      </VuiBox>
      <VuiTypography color="text" variant="caption" display="inline-block">
        100%
      </VuiTypography>
    </VuiBox>
  );

  if (!qualityData) {
    return (
      <Card sx={{ height: "100%", padding: 1 }}>
        <VuiBox display="flex" flexDirection="column" height="100%" p={2}>
          <VuiTypography
            variant="sm"
            color="white"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem", lg: "1.2rem" },
              textAlign: "center",
            }}
          >
            Batch Quality
          </VuiTypography>
          <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%">
            <SemiCircleProgress color="inherit" />
          </VuiBox>
        </VuiBox>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: 1,
        overflow: "visible",
      }}
    >
      
      
      <VuiBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
        p={1}
        sx={{ paddingBottom: 0 }}
      >
        <VuiTypography
          variant="sm"
          color="white"
          fontWeight="bold"
          sx={{
            mb: 0.5,
            fontSize: {
              xs: "0.8rem",
              sm: "0.9rem",
              md: "1rem",
              lg: "1.2rem",
            },
            textAlign: "center",
          }}
        >
          Batch Quality
        </VuiTypography>

        <VuiBox
          display="flex"
          flexDirection={isSmallScreen ? "column" : "row"}
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
          gap={1}
          sx={{
            paddingBottom: 0,
            flexWrap: "wrap",
          }}
        >
          {/* Day Progress */}
          <VuiBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: "1 1 auto",
              width: "100%",
              maxWidth: "300px",
              position: "relative",
            }}
          >
            <SemiCircleProgress
              percentage={qualityData.day.percentage || 0}
              size={{
                width: sizes.day.width,
                height: sizes.day.height,
              }}
              strokeWidth={6}
              strokeColor="#4CAF50"
            />
            <InfoBox
              good={qualityData.day.good || 0}
              bad={qualityData.day.bad || 0}
              label="Day"
              margin={sizes.infoBoxMargin.day}
              sx={{
                width: "90%",
                maxWidth: sizes.day.width * 0.9,
                marginTop: "-70px",
              }}
            />
          </VuiBox>

          {/* Week, Month, Year Progress Grid */}
          <VuiBox
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {/* Week Progress */}
            <VuiBox
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100px",
                minWidth: "100px",
                maxWidth: "100px",
              }}
            >
              <SemiCircleProgress
                percentage={qualityData.week.percentage || 0}
                size={sizes.secondary}
                strokeWidth={6}
                strokeColor="#4CAF50"
              />
              <InfoBox
                good={qualityData.week.good || 0}
                bad={qualityData.week.bad || 0}
                label="Week"
                margin={sizes.infoBoxMargin.secondary}
                sx={{
                  width: "90%",
                  minWidth: "140px",
                }}
              />
            </VuiBox>

            {/* Month Progress */}
            <VuiBox
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100px",
                minWidth: "100px",
                maxWidth: "100px",
              }}
            >
              <SemiCircleProgress
                percentage={qualityData.month.percentage || 0}
                size={sizes.secondary}
                strokeWidth={6}
                strokeColor="#4CAF50"
              />
              <InfoBox
                good={qualityData.month.good || 0}
                bad={qualityData.month.bad || 0}
                label="Month"
                margin={sizes.infoBoxMargin.secondary}
                sx={{
                  width: "90%",
                  minWidth: "140px",
                }}
              />
            </VuiBox>

            {/* Year Progress */}
            <VuiBox
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100px",
                minWidth: "100px",
                maxWidth: "100px",
              }}
            >
              <SemiCircleProgress
                percentage={qualityData.year.percentage || 0}
                size={sizes.secondary}
                strokeWidth={6}
                strokeColor="#4CAF50"
              />
              <InfoBox
                good={qualityData.year.good || 0}
                bad={qualityData.year.bad || 0}
                label="Year"
                margin={sizes.infoBoxMargin.secondary}
                sx={{
                  width: "90%",
                  minWidth: "140px",
                }}
              />
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </VuiBox>

      
    </Card>
  );
};

export default AsphaltBatchQuality;